import { useNavigate } from 'react-router';
import { track } from '@gonfalon/analytics/track';
import {
  enableCheckYourEmailEndpoint,
  enableEmailVerificationCode,
  enforceEmailVerificationLandingPage,
} from '@gonfalon/dogfood-flags/dogfood-flags';
import { toCheckYourEmail } from '@gonfalon/navigator/navigators';

import { navigateAfterSignup, navigateToUnauthenticatedVerifyEmail } from '#actions/navigation';
import { getSignupAttributionSearchParams } from '#components/signUp/agentUtmParams';
import { login } from '#sources/AuthAPI';
import { LoginInfo } from '#utils/loginUtils';
import { isExistingAccountSignupOutcome, type SignupOutcome } from '#utils/signupOutcome';

type CreateUserSuccess = {
  username: string;
  password: string;
  firstName: string;
  lastName: string;
  organization?: string;
  signupOutcome?: SignupOutcome;
};

export type { CreateUserSuccess };

export function useSignUpHandlers() {
  const isEmailVerificationLandingPageEnabled = enforceEmailVerificationLandingPage();
  const isCodeFlowEnabled = enableEmailVerificationCode();

  const navigate = useNavigate();

  const onCreateUnverifiedMemberSuccess = async (account: CreateUserSuccess) => {
    const username = account?.username;

    if (isExistingAccountSignupOutcome(account.signupOutcome) && !!username) {
      if (enableCheckYourEmailEndpoint() || isCodeFlowEnabled) {
        await navigate(toCheckYourEmail({ search: getSignupAttributionSearchParams() }), {
          state: { email: username, signupOutcome: account.signupOutcome },
        });
      } else {
        navigateToUnauthenticatedVerifyEmail(username);
      }
      return;
    }

    track('App Signup Successful', '', {
      email: account.username,
      firstName: account.firstName,
      lastName: account.lastName,
      referrer: window.location.href,
      organization: account.organization,
    });

    if ((isEmailVerificationLandingPageEnabled || isCodeFlowEnabled) && !!username) {
      if (enableCheckYourEmailEndpoint() || isCodeFlowEnabled) {
        await navigate(toCheckYourEmail({ search: getSignupAttributionSearchParams() }), {
          state: { email: username },
        });
      } else {
        navigateToUnauthenticatedVerifyEmail(username);
      }
    } else if (!isEmailVerificationLandingPageEnabled && !isCodeFlowEnabled && !!account) {
      await login(new LoginInfo({ username, password: account.password }));
      navigateAfterSignup(account.username);
    }
  };

  return onCreateUnverifiedMemberSuccess;
}
